import { createFileRoute } from '@tanstack/react-router';
import { FiDownload, FiFileText, FiLock, FiMessageCircle } from 'react-icons/fi';
import { SeoLandingPage } from '@/components/marketing';
import harborSsExportsPng from '@/assets/harbor_ss_exports.png';

function CourtReadyRecordsPage() {
  return (
    <SeoLandingPage
      path='/court-ready-records'
      title='Court-Ready Co-Parenting Records & Exports | Harbor'
      description='Export timestamped messages, expenses, and parenting-time records from Harbor. Court-ready documentation you can send to an attorney — without rebuilding a packet from screenshots.'
      breadcrumbName='Court-ready records'
      eyebrow='Exports & documentation'
      h1='Court-ready records of messages, expenses, and parenting time'
      hero='When counsel or a mediator asks for the paper trail, you should not spend a weekend hunting texts. Harbor keeps timestamped messages, expenses, and calendar history in one place and exports them as a packet.'
      screenEvent='Court Ready Records Screen'
      ctaLocation='Court Ready Records'
      storeCampaign='court-ready-records'
      featuresHeading='Build the file as you go'
      features={[
        {
          icon: FiDownload,
          title: 'One-tap exports, not a screenshot collage',
          description:
            'Select messages, expenses, or calendar records and send a report to email. Harbor is built for court-ready documentation, not a chat app you later try to print.',
          bullets: [
            'PDF and CSV exports',
            'Choose chat, expenses, or calendar',
            'Receive the report by email when you need it',
          ],
          image: harborSsExportsPng,
          imageAlt: 'Harbor export screen for court-ready records',
        },
        {
          icon: FiMessageCircle,
          title: 'Messages that stay on the record',
          description:
            'Co-parent conversations live in Harbor with timestamps. That is the difference between a documented thread and a disappearing iMessage argument.',
          bullets: [
            'Tamper-evident message history',
            'Photos and documents in the same thread',
            'Export the conversation instead of forwarding screenshots',
          ],
        },
        {
          icon: FiFileText,
          title: 'Expenses and calendar in the same packet',
          description:
            'A useful file is more than chat logs. Include the shared-expense ledger and parenting-time calendar so the record matches how the household actually ran.',
          bullets: [
            'Expense and payment history with receipts',
            'Custody calendar records next to the messages',
            'One export workflow instead of three apps',
          ],
        },
        {
          icon: FiLock,
          title: 'A private journal that is not the court file',
          description:
            'Keep personal notes, medical visits, and handoff observations in a journal your co-parent cannot see. Export shared records when you need them; keep the notebook yours.',
          bullets: [
            'Private journal, not visible to the other parent',
            'Shared records stay on the exportable side',
            'You decide what goes in a packet',
          ],
        },
      ]}
      faqs={[
        {
          question: 'What does “court-ready records” mean in Harbor?',
          answer:
            'Timestamped, exportable messages, expenses, and calendar history you can send to an attorney or file with a packet. Harbor does not file documents with a court and is not legal advice.',
        },
        {
          question: 'Can I export only some of my records?',
          answer:
            'Yes. Choose messages, expenses, or calendar — you do not have to dump everything.',
        },
        {
          question: 'Is a Harbor export a certified court record?',
          answer:
            'No. Exports are your documentation from the app. Counsel can decide how to use them. Harbor is not a court clerk and does not certify records.',
        },
        {
          question: 'How do I put Harbor in a parenting plan or order?',
          answer:
            'Use the court-order language templates for communication, expenses, calendar, and document vault wording, then adapt them with an attorney.',
        },
      ]}
      crossLinks={[
        {
          href: '/court-order-language',
          label: 'Court order language templates',
          description:
            'Copy-paste starting points for mandating or recommending Harbor in a parenting plan or order.',
        },
        {
          href: '/learn/coparents',
          label: 'Harbor for co-parents',
          description:
            'See messaging, expenses, calendar, and exports together on the main product page.',
        },
      ]}
    />
  );
}

export const Route = createFileRoute('/court-ready-records')({
  component: CourtReadyRecordsPage,
});
